import React, { useState, useEffect } from 'react';
import {
Box,
Button,
Card,
CardContent,
CircularProgress,
Divider,
FormControl,
Grid,
InputLabel,
MenuItem,
Paper,
Select,
Table,
TableBody,
TableCell,
TableContainer,
TableHead,
TableRow,
TextField,
Typography,
Alert,
Accordion,
AccordionSummary,
AccordionDetails,
Chip,
IconButton,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
FormControlLabel,
Switch,
Tabs,
Tab,
List,
ListItem,
ListItemIcon,
LinearProgress,
Tooltip
} from '@mui/material';
import {
Building2,
CreditCard,
Calendar,
ChevronDown,
Download,
FileText,
Clock,
DollarSign,
User,
Users,
Mail,
Globe,
Phone,
MapPin,
Plus,
Edit,
Trash2,
Search,
CheckCircle,
AlertTriangle,
Package,
BarChart,
Shield,
Info,
Zap,
ArrowUp,
ArrowDown
} from 'lucide-react';
import axios from 'axios';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';

const API_URL = process.env.REACT_APP_API_URL;

// Define subscription plans with separate admin/user pricing
const SUBSCRIPTION_PLANS = [
{
  id: 'free',
  name: 'Free',
  description: 'Basic functionality for small teams',
  adminRate: 0,
  userRate: 0,
  billingOptions: ['monthly', 'annually'],
  annualDiscount: 0,
  minUsers: 1,
  maxUsers: 5,
  features: [
    'Up to 5 team members',
    'Basic reporting',
    'Standard support',
    'Limited storage (5GB)'
  ]
},
{
  id: 'basic',
  name: 'Basic',
  description: 'Essential tools for growing businesses',
  adminRate: 15,
  userRate: 10,
  billingOptions: ['monthly', 'annually'],
  annualDiscount: 0.15, // 15% discount for annual billing
  minUsers: 2,
  maxUsers: 25,
  features: [
    'Up to 25 team members',
    'Advanced reporting',
    'Priority email support',
    'Expanded storage (25GB)',
    'Basic integrations'
  ]
},
{
  id: 'pro',
  name: 'Professional',
  description: 'Advanced features for established organizations',
  adminRate: 25,
  userRate: 15,
  billingOptions: ['monthly', 'annually'],
  annualDiscount: 0.2, // 20% discount for annual billing
  minUsers: 5,
  maxUsers: 100,
  features: [
    'Up to 100 team members',
    'Premium analytics',
    'Priority support with SLA',
    'Expanded storage (100GB)',
    'Advanced integrations',
    'Team training session'
  ]
},
{
  id: 'enterprise',
  name: 'Enterprise',
  description: 'Comprehensive solution for large organizations',
  adminRate: 45,
  userRate: 25,
  billingOptions: ['monthly', 'annually', 'custom'],
  annualDiscount: 0.25, // 25% discount for annual billing
  minUsers: 20,
  maxUsers: null, // Unlimited
  features: [
    'Unlimited team members',
    'Enterprise-grade analytics',
    'Dedicated support manager',
    'Unlimited storage',
    'Custom integrations',
    'Team training sessions',
    'Custom contract terms',
    'SLA guarantees'
  ]
}
];

const CompanyBillingManager = ({ companyId, isAdmin }) => {
// Original states from CompanyBillingManager
const [loading, setLoading] = useState(false);
const [error, setError] = useState(null);
const [saveSuccess, setSaveSuccess] = useState(false);
const [activeAccordion, setActiveAccordion] = useState('billing-details');

// Company info state
const [company, setCompany] = useState(null);
const [users, setUsers] = useState([]);
const [invoices, setInvoices] = useState([]);

// Billing info and user dialog states from original component
const [billingInfo, setBillingInfo] = useState({
  companyName: '',
  contactName: '',
  contactEmail: '',
  vatId: '',
  billingAddress: '',
  paymentMethod: 'invoice',
  planType: 'Basic',
  billingCycle: 'monthly',
  maxUsers: 10,
  nextBillingDate: null
});

const [companyDetails, setCompanyDetails] = useState({
  name: '',
  domain: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  website: '',
});

const [userDialogOpen, setUserDialogOpen] = useState(false);
const [selectedUser, setSelectedUser] = useState(null);
const [userFormData, setUserFormData] = useState({
  name: '',
  email: '',
  role: 'user',
  status: 'active'
});

// New states for enhanced billing functionality
const [activeTab, setActiveTab] = useState(0);
const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
const [confirmDialogAction, setConfirmDialogAction] = useState(null);
const [confirmDialogMessage, setConfirmDialogMessage] = useState('');
const [selectedPlan, setSelectedPlan] = useState(null);
const [billingPreview, setBillingPreview] = useState(null);
const [paymentMethods, setPaymentMethods] = useState([]);
const [addPaymentMethodOpen, setAddPaymentMethodOpen] = useState(false);
const [paymentMethodForm, setPaymentMethodForm] = useState({
  cardNumber: '',
  cardholderName: '',
  expiryDate: '',
  cvv: ''
});

// Get current subscription plan details
const getCurrentPlan = () => {
  const planId = company?.planType?.toLowerCase() || billingInfo.planType.toLowerCase();
  return SUBSCRIPTION_PLANS.find(plan => plan.id === planId) || SUBSCRIPTION_PLANS[1]; // Default to Basic plan
};

// Load company and billing data
useEffect(() => {
  if (companyId) {
    fetchCompanyData();
    fetchCompanyInvoices();
    fetchCompanyUsers();
    fetchPaymentMethods();
  }
}, [companyId]);

// Fetch company data
const fetchCompanyData = async () => {
  try {
    setLoading(true);
    const response = await axios.get(`${API_URL}/api/${isAdmin ? 'admin' : 'companies'}/${companyId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    setCompany(response.data);
    
    // Initialize billing form with company data
    if (response.data) {
      setBillingInfo({
        companyName: response.data.billingInfo?.companyName || response.data.name || '',
        contactName: response.data.billingInfo?.contactName || '',
        contactEmail: response.data.billingInfo?.contactEmail || response.data.email || '',
        vatId: response.data.billingInfo?.vatId || '',
        billingAddress: response.data.billingInfo?.billingAddress || response.data.address || '',
        paymentMethod: response.data.billingInfo?.paymentMethod || 'invoice',
        planType: response.data.planType || 'Basic',
        billingCycle: response.data.billingCycle || 'monthly',
        maxUsers: response.data.maxUsers || 10,
        nextBillingDate: response.data.nextBillingDate ? new Date(response.data.nextBillingDate) : null
      });
      
      // Initialize company details form
      setCompanyDetails({
        name: response.data.name || '',
        domain: response.data.domain || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        address: response.data.address || '',
        city: response.data.city || '',
        state: response.data.state || '',
        zip: response.data.zip || '',
        country: response.data.country || '',
        website: response.data.website || response.data.domain ? `https://${response.data.domain}` : '',
      });
    }
    
    setError(null);
  } catch (err) {
    console.error('Error fetching company data:', err);
    setError('Failed to load company data');
    
    // If in development mode, provide mock data
    if (process.env.NODE_ENV === 'development') {
      const mockCompany = generateMockCompany();
      setCompany(mockCompany);
      setBillingInfo({
        companyName: mockCompany.name,
        contactName: 'John Smith',
        contactEmail: 'billing@example.com',
        vatId: 'VAT12345',
        billingAddress: '123 Business St, City, Country',
        paymentMethod: 'invoice',
        planType: mockCompany.planType,
        billingCycle: mockCompany.billingCycle,
        maxUsers: mockCompany.maxUsers,
        nextBillingDate: new Date(new Date().setMonth(new Date().getMonth() + 1))
      });
      
      setCompanyDetails({
        name: mockCompany.name,
        domain: mockCompany.domain,
        email: mockCompany.email,
        phone: mockCompany.phone,
        address: mockCompany.address,
        city: mockCompany.city,
        state: mockCompany.state,
        zip: mockCompany.zip,
        country: mockCompany.country,
        website: mockCompany.website,
      });
    }
  } finally {
    setLoading(false);
  }
};

// Fetch company invoices
const fetchCompanyInvoices = async () => {
  try {
    setLoading(true);
    const response = await axios.get(`${API_URL}/api/${isAdmin ? 'admin/' : ''}companies/${companyId}/invoices`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    setInvoices(response.data || []);
  } catch (err) {
    console.error('Error fetching company invoices:', err);
    
    // If in development mode, provide mock data
    if (process.env.NODE_ENV === 'development') {
      setInvoices(generateMockInvoices());
    }
  } finally {
    setLoading(false);
  }
};

// Fetch company users
const fetchCompanyUsers = async () => {
  try {
    setLoading(true);
    const response = await axios.get(`${API_URL}/api/${isAdmin ? 'admin/' : ''}companies/${companyId}/users`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    setUsers(response.data || []);
  } catch (err) {
    console.error('Error fetching company users:', err);
    
    // If in development mode, provide mock data
    if (process.env.NODE_ENV === 'development') {
      setUsers(generateMockUsers());
    }
  } finally {
    setLoading(false);
  }
};

// Fetch payment methods
const fetchPaymentMethods = async () => {
  try {
    setLoading(true);
    const response = await axios.get(`${API_URL}/api/${isAdmin ? 'admin/' : ''}companies/${companyId}/payment-methods`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    
    setPaymentMethods(response.data || []);
  } catch (err) {
    console.error('Error fetching payment methods:', err);
    
    // If in development mode, provide mock data
    if (process.env.NODE_ENV === 'development') {
      setPaymentMethods([
        {
          id: 'pm-001',
          type: 'visa',
          last4: '4242',
          expMonth: 3,
          expYear: 2026,
          isDefault: true
        }
      ]);
    }
  } finally {
    setLoading(false);
  }
};

// Generate mock company for development
const generateMockCompany = () => {
  return {
    id: companyId,
    name: 'Example Corp',
    domain: 'example.com',
    email: 'info@example.com',
    phone: '+1 (555) 123-4567',
    address: '123 Main St',
    city: 'San Francisco',
    state: 'CA',
    zip: '94105',
    country: 'USA',
    status: 'active',
    planType: 'Pro',
    maxUsers: 25,
    billingCycle: 'annually',
    website: 'https://example.com',
    billingInfo: {
      companyName: 'Example Corp',
      contactName: 'John Smith',
      contactEmail: 'billing@example.com',
      vatId: 'VAT12345',
      billingAddress: '123 Business St, City, Country',
      paymentMethod: 'invoice'
    }
  };
};

// Generate mock invoices for development
const generateMockInvoices = () => {
  return [
    { 
      id: 'INV-2023-001', 
      date: '2023-01-15', 
      amount: 4800.00, 
      status: 'paid', 
      description: 'Annual subscription - Pro Plan (25 users)', 
      lineItems: [
        { description: 'Admin licenses (3)', unitPrice: 25.00, quantity: 3, discount: 0.2, total: 720.00 },
        { description: 'User licenses (22)', unitPrice: 15.00, quantity: 22, discount: 0.2, total: 3168.00 },
        { description: 'Extra storage (100GB)', unitPrice: 10.00, quantity: 12, discount: 0, total: 120.00 }
      ],
      subtotal: 4008.00,
      discount: 0,
      tax: 792.00,
      total: 4800.00,
      periodStart: '2023-01-15',
      periodEnd: '2024-01-14'
    },
    { 
      id: 'INV-2023-002', 
      date: '2023-02-15', 
      amount: 300.00, 
      status: 'paid', 
      description: 'Additional user licenses (5 users)', 
      lineItems: [
        { description: 'User licenses (5)', unitPrice: 15.00, quantity: 5, discount: 0, total: 75.00 },
        { description: 'One-time setup fee', unitPrice: 225.00, quantity: 1, discount: 0, total: 225.00 }
      ],
      subtotal: 300.00,
      discount: 0,
      tax: 0,
      total: 300.00
    },
    { 
      id: 'INV-2023-003', 
      date: '2023-03-15', 
      amount: 5200.00, 
      status: 'pending', 
      description: 'Annual subscription renewal - Pro Plan (27 users + add-ons)', 
      lineItems: [
        { description: 'Admin licenses (3)', unitPrice: 25.00, quantity: 3, discount: 0.2, total: 720.00 },
        { description: 'User licenses (24)', unitPrice: 15.00, quantity: 24, discount: 0.2, total: 3456.00 },
        { description: 'Extra storage (150GB)', unitPrice: 10.00, quantity: 15, discount: 0, total: 150.00 },
        { description: 'API access add-on', unitPrice: 50.00, quantity: 12, discount: 0, total: 600.00 }
      ],
      subtotal: 4926.00,
      discount: 0,
      tax: 274.00,
      total: 5200.00,
      periodStart: '2024-01-15',
      periodEnd: '2025-01-14'
    }
  ];
};

// Generate mock users for development
const generateMockUsers = () => {
  return [
    { id: 1, name: 'John Smith', email: 'john@example.com', role: 'admin', status: 'active', lastLogin: '2023-03-10T14:30:00Z' },
    { id: 2, name: 'Sarah Johnson', email: 'sarah@example.com', role: 'admin', status: 'active', lastLogin: '2023-03-12T09:15:00Z' },
    { id: 3, name: 'Mike Williams', email: 'mike@example.com', role: 'user', status: 'active', lastLogin: '2023-03-08T11:45:00Z' },
    { id: 4, name: 'Emily Davis', email: 'emily@example.com', role: 'user', status: 'active', lastLogin: '2023-03-08T11:45:00Z' },
    { id: 5, name: 'Robert Jones', email: 'robert@example.com', role: 'user', status: 'active', lastLogin: '2023-03-08T11:45:00Z' },
    { id: 6, name: 'Lisa Brown', email: 'lisa@example.com', role: 'user', status: 'inactive', lastLogin: '2023-02-28T16:20:00Z' }
  ];
};
const renderCompanyProfileTab = () => {
  return (
    <Box>
      <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
        Company and Billing Information
      </Typography>

      {/* Company Profile Card */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 3 }}>
            <Building2 size={18} style={{ marginRight: 8, verticalAlign: 'text-bottom' }} />
            Company Profile
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="name"
                label="Company Name"
                value={companyDetails.name}
                onChange={handleCompanyDetailsChange}
                fullWidth
                required
                InputProps={{
                  startAdornment: <Building2 size={18} style={{ marginRight: 8, opacity: 0.7 }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="domain"
                label="Domain"
                value={companyDetails.domain}
                onChange={handleCompanyDetailsChange}
                fullWidth
                InputProps={{
                  startAdornment: <Globe size={18} style={{ marginRight: 8, opacity: 0.7 }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="email"
                label="Company Email"
                value={companyDetails.email}
                onChange={handleCompanyDetailsChange}
                fullWidth
                InputProps={{
                  startAdornment: <Mail size={18} style={{ marginRight: 8, opacity: 0.7 }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="phone"
                label="Phone Number"
                value={companyDetails.phone}
                onChange={handleCompanyDetailsChange}
                fullWidth
                InputProps={{
                  startAdornment: <Phone size={18} style={{ marginRight: 8, opacity: 0.7 }} />
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="address"
                label="Address"
                value={companyDetails.address}
                onChange={handleCompanyDetailsChange}
                fullWidth
                InputProps={{
                  startAdornment: <MapPin size={18} style={{ marginRight: 8, opacity: 0.7 }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="city"
                label="City"
                value={companyDetails.city}
                onChange={handleCompanyDetailsChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="state"
                label="State/Province"
                value={companyDetails.state}
                onChange={handleCompanyDetailsChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                name="zip"
                label="Postal/ZIP Code"
                value={companyDetails.zip}
                onChange={handleCompanyDetailsChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="country"
                label="Country"
                value={companyDetails.country}
                onChange={handleCompanyDetailsChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="website"
                label="Website"
                value={companyDetails.website}
                onChange={handleCompanyDetailsChange}
                fullWidth
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleSubmitCompanyDetails}
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              Save Company Details
            </Button>
          </Box>
        </CardContent>
      </Card>
      
      {/* Billing Information Card */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 3 }}>
            <FileText size={18} style={{ marginRight: 8, verticalAlign: 'text-bottom' }} />
            Billing Information
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="companyName"
                label="Billing Company Name"
                value={billingInfo.companyName}
                onChange={handleBillingInfoChange}
                fullWidth
                InputProps={{
                  startAdornment: <Building2 size={18} style={{ marginRight: 8, opacity: 0.7 }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="contactName"
                label="Billing Contact Name"
                value={billingInfo.contactName}
                onChange={handleBillingInfoChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="contactEmail"
                label="Billing Email"
                value={billingInfo.contactEmail}
                onChange={handleBillingInfoChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="vatId"
                label="VAT/Tax ID"
                value={billingInfo.vatId}
                onChange={handleBillingInfoChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="billingAddress"
                label="Billing Address"
                value={billingInfo.billingAddress}
                onChange={handleBillingInfoChange}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleSubmitBilling}
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              Save Billing Information
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
// Handle tab change - set default tab to 3 (Company Profile) on first load
useEffect(() => {
  if (company && !activeTab) {
    setActiveTab(0); // Start with subscription tab by default after data is loaded
  }
}, [company]);

// Handle tab change
const handleTabChange = (event, newValue) => {
  setActiveTab(newValue);
};

// Handle billing info changes
const handleBillingInfoChange = (e) => {
  const { name, value } = e.target;
  setBillingInfo(prev => ({
    ...prev,
    [name]: value
  }));
};

// Handle company details changes
const handleCompanyDetailsChange = (e) => {
  const { name, value } = e.target;
  setCompanyDetails(prev => ({
    ...prev,
    [name]: value
  }));
};

// Handle next billing date change
const handleNextBillingDateChange = (date) => {
  setBillingInfo(prev => ({
    ...prev,
    nextBillingDate: date
  }));
};

// Handle accordion change
const handleAccordionChange = (panel) => (event, isExpanded) => {
  setActiveAccordion(isExpanded ? panel : false);
};

// Calculate current billing metrics
const calculateBillingMetrics = () => {
  const currentPlan = getCurrentPlan();
  const adminCount = users.filter(user => user.role === 'admin' && user.status === 'active').length;
  const userCount = users.filter(user => user.role === 'user' && user.status === 'active').length;
  const isAnnual = billingInfo.billingCycle === 'annually';
  const discount = isAnnual ? currentPlan.annualDiscount : 0;
  
  // Calculate costs
  const adminCost = adminCount * currentPlan.adminRate * (1 - discount);
  const userCost = userCount * currentPlan.userRate * (1 - discount);
  const monthlyTotal = adminCost + userCost;
  const annualTotal = monthlyTotal * 12;
  
  return {
    adminCount,
    userCount,
    totalUsers: adminCount + userCount,
    adminCost,
    userCost,
    monthlyTotal,
    annualTotal,
    isAnnual,
    discount
  };
};

// Open plan change dialog
const handleOpenChangePlanDialog = () => {
  // Default to current plan
  const currentPlanId = billingInfo.planType.toLowerCase();
  setSelectedPlan(currentPlanId);
  updateBillingPreview(currentPlanId, billingInfo.billingCycle);
  setChangePlanDialogOpen(true);
};

// Close plan change dialog
const handleCloseChangePlanDialog = () => {
  setChangePlanDialogOpen(false);
  setSelectedPlan(null);
  setBillingPreview(null);
};

// Handle plan selection change
const handlePlanChange = (e) => {
  const newPlanId = e.target.value;
  setSelectedPlan(newPlanId);
  updateBillingPreview(newPlanId, billingPreview?.billingCycle || billingInfo.billingCycle);
};

// Handle billing cycle change in preview
const handleBillingCycleChange = (e) => {
  const newCycle = e.target.value;
  updateBillingPreview(selectedPlan, newCycle);
};

// Update billing preview calculations
const updateBillingPreview = (planId, billingCycle) => {
  const selectedPlanDetails = SUBSCRIPTION_PLANS.find(plan => plan.id === planId);
  if (!selectedPlanDetails) return;
  
  const adminCount = users.filter(user => user.role === 'admin' && user.status === 'active').length;
  const userCount = users.filter(user => user.role === 'user' && user.status === 'active').length;
  const isAnnual = billingCycle === 'annually';
  const discount = isAnnual ? selectedPlanDetails.annualDiscount : 0;
  
  // Calculate costs
  const adminCost = adminCount * selectedPlanDetails.adminRate * (1 - discount);
  const userCost = userCount * selectedPlanDetails.userRate * (1 - discount);
  const monthlyTotal = adminCost + userCost;
  const annualTotal = monthlyTotal * 12;
  
  // Calculate current costs for comparison
  const currentMetrics = calculateBillingMetrics();
  const monthlySavings = currentMetrics.monthlyTotal - monthlyTotal;
  const annualSavings = currentMetrics.annualTotal - annualTotal;
  
  setBillingPreview({
    planId,
    planName: selectedPlanDetails.name,
    billingCycle,
    adminCount,
    userCount,
    totalUsers: adminCount + userCount,
    adminRate: selectedPlanDetails.adminRate,
    userRate: selectedPlanDetails.userRate,
    adminCost,
    userCost,
    discount,
    monthlyTotal,
    annualTotal,
    isAnnual,
    monthlySavings,
    annualSavings,
    minUsers: selectedPlanDetails.minUsers,
    userShortage: Math.max(0, selectedPlanDetails.minUsers - (adminCount + userCount))
  });
};

// Confirm plan change
const handleConfirmPlanChange = async () => {
  if (!billingPreview) return;
  
  try {
    setLoading(true);
    
    // Prepare request data
    const requestData = {
      planType: billingPreview.planName,
      billingCycle: billingPreview.billingCycle,
      maxUsers: Math.max(billingPreview.totalUsers, billingPreview.minUsers),
      adminCount: billingPreview.adminCount,
      userCount: billingPreview.userCount
    };
    
    // API call to update plan
    await axios.put(`${API_URL}/api/${isAdmin ? 'admin/' : ''}companies/${companyId}/plan`, 
      requestData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    
    // Update local state
    setBillingInfo(prev => ({
      ...prev,
      planType: billingPreview.planName,
      billingCycle: billingPreview.billingCycle,
      maxUsers: Math.max(billingPreview.totalUsers, billingPreview.minUsers)
    }));
    
    setCompany(prev => ({
      ...prev,
      planType: billingPreview.planName,
      billingCycle: billingPreview.billingCycle,
      maxUsers: Math.max(billingPreview.totalUsers, billingPreview.minUsers)
    }));
    
    // Show success message
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 5000);
    
    // Close dialogs
    setChangePlanDialogOpen(false);
    
    // Refresh data
    fetchCompanyData();
    fetchCompanyInvoices();
    
  } catch (error) {
    console.error('Error updating plan:', error);
    setError('Failed to update subscription plan. Please try again.');
  } finally {
    setLoading(false);
  }
};

// Open add payment method dialog
const handleOpenAddPaymentMethod = () => {
  setPaymentMethodForm({
    cardNumber: '',
    cardholderName: '',
    expiryDate: '',
    cvv: ''
  });
  setAddPaymentMethodOpen(true);
};

// Close add payment method dialog
const handleCloseAddPaymentMethod = () => {
  setAddPaymentMethodOpen(false);
};

// Handle payment method form changes
const handlePaymentMethodFormChange = (e) => {
  const { name, value } = e.target;
  setPaymentMethodForm(prev => ({
    ...prev,
    [name]: value
  }));
};

// Add new payment method
const handleAddPaymentMethod = async () => {
  try {
    setLoading(true);
    
    // Prepare request data
    const requestData = {
      ...paymentMethodForm,
      isDefault: paymentMethods.length === 0 // Set as default if it's the first one
    };
    
    // API call to add payment method
    const response = await axios.post(`${API_URL}/api/${isAdmin ? 'admin/' : ''}companies/${companyId}/payment-methods`, 
      requestData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    
    // Update local state
    if (response.data) {
      setPaymentMethods(prev => [...prev, response.data]);
    }
    
    // Show success message
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 5000);
    
    // Close dialog
    setAddPaymentMethodOpen(false);
    
  } catch (error) {
    console.error('Error adding payment method:', error);
    setError('Failed to add payment method. Please try again.');
  } finally {
    setLoading(false);
  }
};

// Make a payment method default
const handleSetDefaultPaymentMethod = async (paymentMethodId) => {
  try {
    setLoading(true);
    
    // API call to set default payment method
    await axios.put(`${API_URL}/api/${isAdmin ? 'admin/' : ''}companies/${companyId}/payment-methods/${paymentMethodId}/default`, 
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    
    // Update local state
    setPaymentMethods(prev =>
      prev.map(method => ({
        ...method,
        isDefault: method.id === paymentMethodId
      }))
    );
    
    // Show success message
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 5000);
    
  } catch (error) {
    console.error('Error setting default payment method:', error);
    setError('Failed to update default payment method. Please try again.');
  } finally {
    setLoading(false);
  }
};

// Delete payment method
const handleDeletePaymentMethod = async (paymentMethodId) => {
  try {
    setLoading(true);
    
    // Check if this is the default payment method
    const isDefault = paymentMethods.find(method => method.id === paymentMethodId)?.isDefault;
    
    if (isDefault && paymentMethods.length > 1) {
      setError('Cannot delete the default payment method. Please set another method as default first.');
      setLoading(false);
      return;
    }
    
    // API call to delete payment method
    await axios.delete(`${API_URL}/api/${isAdmin ? 'admin/' : ''}companies/${companyId}/payment-methods/${paymentMethodId}`, 
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    
    // Update local state
    setPaymentMethods(prev => prev.filter(method => method.id !== paymentMethodId));
    
    // Show success message
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 5000);
    
  } catch (error) {
    console.error('Error deleting payment method:', error);
    setError('Failed to delete payment method. Please try again.');
  } finally {
    setLoading(false);
  }
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

// Get invoice status color
const getInvoiceStatusColor = (status) => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'pending':
      return 'warning';
    case 'overdue':
      return 'error';
    default:
      return 'default';
  }
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    return dateString;
  }
};

// Count active users by role
const countActiveUsersByRole = () => {
  const adminCount = users.filter(user => user.role === 'admin' && user.status === 'active').length;
  const userCount = users.filter(user => user.role === 'user' && user.status === 'active').length;
  return { adminCount, userCount, totalActive: adminCount + userCount };
};

// Render billing dashboard tabs
const renderBillingDashboardTabs = () => {
  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab 
            icon={<CreditCard size={18} />}
            iconPosition="start"
            label="Subscription" 
          />
          <Tab 
            icon={<FileText size={18} />}
            iconPosition="start"
            label="Invoices" 
          />
          <Tab 
            icon={<Users size={18} />}
            iconPosition="start"
            label="User Management" 
          />
          <Tab 
            icon={<Building2 size={18} />}
            iconPosition="start"
            label="Company Profile" 
          />
        </Tabs>
      </Paper>
    </Box>
  );
};

// Render subscription overview
const renderSubscriptionOverview = () => {
  const currentPlan = getCurrentPlan();
  const metrics = calculateBillingMetrics();
  const { adminCount, userCount } = countActiveUsersByRole();
  
  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h5" component="h2" fontWeight="bold">
              {currentPlan.name} Plan
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currentPlan.description}
            </Typography>
          </Box>
          <Chip 
            label={company?.status || 'active'} 
            color={company?.status === 'active' ? 'success' : 
                   company?.status === 'trial' ? 'info' : 'default'} 
            size="small" 
          />
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Billing Cycle
              </Typography>
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Calendar size={16} />
                {billingInfo.billingCycle === 'monthly' ? 'Monthly' : 'Annual'} billing
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Next billing: {formatDate(billingInfo.nextBillingDate)}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Active Users
              </Typography>
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Users size={16} />
                {adminCount + userCount} of {billingInfo.maxUsers} licenses used
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {adminCount} admins, {userCount} regular users
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Monthly Cost
              </Typography>
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DollarSign size={16} />
                {formatCurrency(metrics.monthlyTotal)}
                {metrics.isAnnual && (
                  <Chip size="small" label={`${Math.round(metrics.discount * 100)}% annual discount`} color="success" />
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Admin: {formatCurrency(metrics.adminCost)}, User: {formatCurrency(metrics.userCost)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Package size={16} />
            <Typography variant="body2">
              Plan Features: {currentPlan.features.slice(0, 2).join(", ")}...
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<Edit size={16} />}
            onClick={handleOpenChangePlanDialog}
          >
            Change Plan
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

// Render subscription tab content
const renderSubscriptionTab = () => {
  return (
    <Box>
      {renderSubscriptionOverview()}
      
      <Grid container spacing={3}>
        {/* Current Plan */}
        <Grid item xs={12} md={8}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                Plan Details & Billing
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Billing Cycle</InputLabel>
                    <Select
                      name="billingCycle"
                      value={billingInfo.billingCycle}
                      onChange={handleBillingInfoChange}
                      label="Billing Cycle"
                    >
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="annually">Annual (Save {Math.round(getCurrentPlan().annualDiscount * 100)}%)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Maximum Users</InputLabel>
                    <Select
                      name="maxUsers"
                      value={billingInfo.maxUsers}
                      onChange={handleBillingInfoChange}
                      label="Maximum Users"
                    >
                      {[5, 10, 25, 50, 100, 250, 500].map(count => (
                        <MenuItem key={count} value={count}>{count} users</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      name="paymentMethod"
                      value={billingInfo.paymentMethod}
                      onChange={handleBillingInfoChange}
                      label="Payment Method"
                    >
                      <MenuItem value="credit_card">Credit Card</MenuItem>
                      <MenuItem value="invoice">Invoice</MenuItem>
                      <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Next Billing Date"
                      value={billingInfo.nextBillingDate}
                      onChange={handleNextBillingDateChange}
                      renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                Current Plan Features
              </Typography>
              
              <Grid container spacing={1}>
                {getCurrentPlan().features.map((feature, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle size={16} color="#4CAF50" />
                      <Typography variant="body2">{feature}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleSubmitBilling}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                  Save Billing Changes
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Payment Methods */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h3">
                  Payment Methods
                </Typography>
                <Button 
                  variant="outlined" 
                  startIcon={<Plus size={16} />}
                  onClick={handleOpenAddPaymentMethod}
                  size="small"
                >
                  Add New
                </Button>
              </Box>
              
              {paymentMethods.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  No payment methods found. Add a payment method to manage your subscription.
                </Alert>
              ) : (
                <List disablePadding>
                  {paymentMethods.map((method) => (
                    <ListItem
                      key={method.id}
                      disablePadding
                      secondaryAction={
                        <Box>
                          {!method.isDefault && (
                            <>
                              <Tooltip title="Set as default">
                                <IconButton
                                  edge="end"
                                  onClick={() => handleSetDefaultPaymentMethod(method.id)}
                                  size="small"
                                >
                                  <CheckCircle size={16} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton 
                                  edge="end" 
                                  color="error"
                                  onClick={() => handleDeletePaymentMethod(method.id)}
                                  size="small"
                                >
                                  <Trash2 size={16} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      }
                      sx={{ py: 1, borderBottom: '1px solid #eee' }}
                    >
                      <ListItemIcon>
                        <CreditCard size={18} />
                      </ListItemIcon>
                      <Box>
                        <Typography variant="body2">
                          {method.type.charAt(0).toUpperCase() + method.type.slice(1)} •••• {method.last4}
                          {method.isDefault && (
                            <Chip 
                              label="Default" 
                              size="small" 
                              color="primary" 
                              sx={{ ml: 1 }} 
                            />
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Expires {method.expMonth}/{method.expYear}
                        </Typography>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// Render invoices tab content
const renderInvoicesTab = () => {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" component="h3" sx={{ mb: 3 }}>
          Invoice History
        </Typography>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.length > 0 ? (
                invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.id}</TableCell>
                    <TableCell>{formatDate(invoice.date)}</TableCell>
                    <TableCell>{invoice.description}</TableCell>
                    <TableCell align="right">{formatCurrency(invoice.amount)}</TableCell>
                    <TableCell>
                      <Chip 
                        size="small"
                        label={invoice.status}
                        color={getInvoiceStatusColor(invoice.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small" color="primary">
                          <FileText size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton size="small" color="primary">
                          <Download size={16} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No invoices found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

// Render user management tab content
const renderUsersTab = () => {
  const { adminCount, userCount, totalActive } = countActiveUsersByRole();
  const currentPlan = getCurrentPlan();
  
  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
            User License Usage
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Admin Licenses
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">{adminCount}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    × {formatCurrency(currentPlan.adminRate)}/user/month
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Regular User Licenses
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">{userCount}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    × {formatCurrency(currentPlan.userRate)}/user/month
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Usage
                </Typography>
                <Typography variant="h6">
                  {totalActive} of {billingInfo.maxUsers} licenses used
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(totalActive / billingInfo.maxUsers) * 100}
                  sx={{ mt: 1, height: 8, borderRadius: 4 }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* User Management */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h3">
              Manage Users
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Plus size={16} />}
              onClick={() => handleOpenUserDialog()}
              size="small"
            >
              Add User
            </Button>
          </Box>
          
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              placeholder="Search users..."
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: <Search size={18} style={{ marginRight: 8, opacity: 0.7 }} />
              }}
              sx={{ flexGrow: 1 }}
            />
          </Box>
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length > 0 ? (
                  users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={user.role}
                          color={user.role === 'admin' ? 'secondary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={user.status}
                          color={user.status === 'active' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit User">
                            <IconButton size="small" color="primary" onClick={() => handleOpenUserDialog(user)}>
                              <Edit size={16} />
                            </IconButton>
                          </Tooltip>
                          <Button
                            size="small"
                            variant="outlined"
                            color={user.status === 'active' ? 'error' : 'success'}
                            onClick={() => toggleUserStatus(user.id, user.status)}
                          >
                            {user.status === 'active' ? 'Deactivate' : 'Activate'}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No users found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

// Toggle user status
const toggleUserStatus = async (userId, currentStatus) => {
  try {
    setLoading(true);
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    
    await axios.put(`${API_URL}/api/${isAdmin ? 'admin/' : ''}users/${userId}/status`,
      { status: newStatus },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    
    // Update local users state
    setUsers(prevUsers =>
      prevUsers.map(user =>
        user.id === userId ? { ...user, status: newStatus } : user
      )
    );
    
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
    setError(null);
  } catch (err) {
    console.error('Error updating user status:', err);
    setError('Failed to update user status');
  } finally {
    setLoading(false);
  }
};

// Open user dialog for editing or creating
const handleOpenUserDialog = (user = null) => {
  if (user) {
    // Edit mode
    setSelectedUser(user);
    setUserFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      status: user.status
    });
  } else {
    // Create mode
    setSelectedUser(null);
    setUserFormData({
      name: '',
      email: '',
      role: 'user',
      status: 'active'
    });
  }
  setUserDialogOpen(true);
};

// Close user dialog
const handleCloseUserDialog = () => {
  setUserDialogOpen(false);
  setSelectedUser(null);
};

// Handle user form changes
const handleUserFormChange = (e) => {
  const { name, value } = e.target;
  setUserFormData(prev => ({
    ...prev,
    [name]: value
  }));
};

// Submit user form
const handleUserFormSubmit = async () => {
  try {
    setLoading(true);
    
    if (selectedUser) {
      // Update existing user
      await axios.put(`${API_URL}/api/${isAdmin ? 'admin/' : ''}users/${selectedUser.id}`,
        userFormData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      // Update local state
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === selectedUser.id ? { ...user, ...userFormData } : user
        )
      );
    } else {
      // Create new user
      const response = await axios.post(`${API_URL}/api/${isAdmin ? 'admin/' : ''}companies/${companyId}/users`,
        userFormData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      // Add to local state
      setUsers(prevUsers => [...prevUsers, response.data]);
    }
    
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
    setError(null);
    handleCloseUserDialog();
  } catch (err) {
    console.error('Error saving user:', err);
    setError(err.response?.data?.message || 'Failed to save user');
  } finally {
    setLoading(false);
  }
};

// Handle form submission for billing info
const handleSubmitBilling = async () => {
  try {
    setLoading(true);
    setSaveSuccess(false);
    
    // Format data for API
    const apiData = {
      billingInfo: {
        companyName: billingInfo.companyName,
        contactName: billingInfo.contactName,
        contactEmail: billingInfo.contactEmail,
        vatId: billingInfo.vatId,
        billingAddress: billingInfo.billingAddress,
        paymentMethod: billingInfo.paymentMethod
      },
      planType: billingInfo.planType,
      billingCycle: billingInfo.billingCycle,
      maxUsers: billingInfo.maxUsers,
      nextBillingDate: billingInfo.nextBillingDate ? billingInfo.nextBillingDate.toISOString() : null
    };
    
    // Update company billing info
    await axios.put(
      `${API_URL}/api/${isAdmin ? 'admin/' : ''}companies/${companyId}/billing`, 
      apiData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    
    setSaveSuccess(true);
    setError(null);
    
    // Refresh company data after short delay
    setTimeout(() => {
      fetchCompanyData();
      setSaveSuccess(false);
    }, 3000);
    
  } catch (err) {
    console.error('Error saving billing info:', err);
    setError(err.response?.data?.message || 'Failed to save billing information');
  } finally {
    setLoading(false);
  }
};

// Handle form submission for company details
const handleSubmitCompanyDetails = async () => {
  try {
    setLoading(true);
    setSaveSuccess(false);
    
    // Update company details
    await axios.put(
      `${API_URL}/api/${isAdmin ? 'admin/' : ''}companies/${companyId}`, 
      companyDetails,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    );
    
    setSaveSuccess(true);
    setError(null);
    
    // Refresh company data after short delay
    setTimeout(() => {
      fetchCompanyData();
      setSaveSuccess(false);
    }, 3000);
    
  } catch (err) {
    console.error('Error saving company details:', err);
    setError(err.response?.data?.message || 'Failed to save company details');
  } finally {
    setLoading(false);
  }
};

// Main render function
if (loading && !company) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
      <CircularProgress />
    </Box>
  );
}

return (
  <Box>
    {/* Page Header */}
    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Building2 size={24} />
        <Typography variant="h5" component="h1">
          {company?.name || 'Company'} Management
        </Typography>
      </Box>
    </Box>
    
    {/* Success message */}
    {saveSuccess && (
      <Alert severity="success" sx={{ mb: 3 }}>
        Information updated successfully!
      </Alert>
    )}
    
    {/* Error message */}
    {error && (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    )}
    
    {/* Billing Dashboard Tabs */}
    {renderBillingDashboardTabs()}
    
    {/* Tab Content */}
    {activeTab === 0 && renderSubscriptionTab()}
    {activeTab === 1 && renderInvoicesTab()}
    {activeTab === 2 && renderUsersTab()}
    {activeTab === 3 && renderCompanyProfileTab()}
    
    {/* Original Company Structure */}
    <Accordion
      expanded={activeAccordion === 'company-details'}
      onChange={handleAccordionChange('company-details')}
      elevation={0}
      sx={{ border: '1px solid #e0e0e0', mt: 3, mb: 2, borderRadius: 1 }}
    >
      <AccordionSummary expandIcon={<ChevronDown />}>
        <Typography variant="subtitle1" fontWeight="medium">Company Profile</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              name="name"
              label="Company Name"
              value={companyDetails.name}
              onChange={handleCompanyDetailsChange}
              fullWidth
              required
              InputProps={{
                startAdornment: <Building2 size={18} style={{ marginRight: 8, opacity: 0.7 }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="domain"
              label="Domain"
              value={companyDetails.domain}
              onChange={handleCompanyDetailsChange}
              fullWidth
              InputProps={{
                startAdornment: <Globe size={18} style={{ marginRight: 8, opacity: 0.7 }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="email"
              label="Company Email"
              value={companyDetails.email}
              onChange={handleCompanyDetailsChange}
              fullWidth
              InputProps={{
                startAdornment: <Mail size={18} style={{ marginRight: 8, opacity: 0.7 }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="phone"
              label="Phone Number"
              value={companyDetails.phone}
              onChange={handleCompanyDetailsChange}
              fullWidth
              InputProps={{
                startAdornment: <Phone size={18} style={{ marginRight: 8, opacity: 0.7 }} />
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="address"
              label="Address"
              value={companyDetails.address}
              onChange={handleCompanyDetailsChange}
              fullWidth
              InputProps={{
                startAdornment: <MapPin size={18} style={{ marginRight: 8, opacity: 0.7 }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              name="city"
              label="City"
              value={companyDetails.city}
              onChange={handleCompanyDetailsChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              name="state"
              label="State/Province"
              value={companyDetails.state}
              onChange={handleCompanyDetailsChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              name="zip"
              label="Postal/ZIP Code"
              value={companyDetails.zip}
              onChange={handleCompanyDetailsChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="country"
              label="Country"
              value={companyDetails.country}
              onChange={handleCompanyDetailsChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="website"
              label="Website"
              value={companyDetails.website}
              onChange={handleCompanyDetailsChange}
              fullWidth
            />
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSubmitCompanyDetails}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            Save Company Details
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
    
    <Accordion
      expanded={activeAccordion === 'billing-details'}
      onChange={handleAccordionChange('billing-details')}
      elevation={0}
      sx={{ border: '1px solid #e0e0e0', mb: 2, borderRadius: 1 }}
    >
      <AccordionSummary expandIcon={<ChevronDown />}>
        <Typography variant="subtitle1" fontWeight="medium">Billing Information</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              name="companyName"
              label="Billing Company Name"
              value={billingInfo.companyName}
              onChange={handleBillingInfoChange}
              fullWidth
              InputProps={{
                startAdornment: <Building2 size={18} style={{ marginRight: 8, opacity: 0.7 }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="contactName"
              label="Billing Contact Name"
              value={billingInfo.contactName}
              onChange={handleBillingInfoChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="contactEmail"
              label="Billing Email"
              value={billingInfo.contactEmail}
              onChange={handleBillingInfoChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              name="vatId"
              label="VAT/Tax ID"
              value={billingInfo.vatId}
              onChange={handleBillingInfoChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="billingAddress"
              label="Billing Address"
              value={billingInfo.billingAddress}
              onChange={handleBillingInfoChange}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSubmitBilling}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            Save Billing Information
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
    
    {/* Change Plan Dialog */}
    <Dialog 
      open={changePlanDialogOpen} 
      onClose={handleCloseChangePlanDialog}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Change Subscription Plan
      </DialogTitle>
      <DialogContent dividers>
        {billingPreview?.userShortage > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            The selected plan requires a minimum of {billingPreview.minUsers} users. 
            You currently have {billingPreview.totalUsers} active users. 
            You'll need to add {billingPreview.userShortage} more users before upgrading.
          </Alert>
        )}
        
        <Typography variant="subtitle1" gutterBottom>
          Select a Plan:
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Subscription Plan</InputLabel>
          <Select
            value={selectedPlan || ''}
            onChange={handlePlanChange}
            label="Subscription Plan"
          >
            {SUBSCRIPTION_PLANS.map(plan => (
              <MenuItem key={plan.id} value={plan.id}>
                {plan.name} Plan - Admin: ${plan.adminRate}/user/mo, User: ${plan.userRate}/user/mo
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {billingPreview && (
          <>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Billing Cycle</InputLabel>
              <Select
                value={billingPreview.billingCycle}
                onChange={handleBillingCycleChange}
                label="Billing Cycle"
              >
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="annually">
                  Annual ({Math.round(SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan)?.annualDiscount * 100)}% discount)
                </MenuItem>
              </Select>
            </FormControl>
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="h6" gutterBottom>
              Billing Preview:
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Admin Licenses
                  </Typography>
                  <Typography variant="body1">
                    {billingPreview.adminCount} × ${billingPreview.adminRate}/month
                    {billingPreview.discount > 0 && ` (${Math.round(billingPreview.discount * 100)}% off)`}
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(billingPreview.adminCost)}/month
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    User Licenses
                  </Typography>
                  <Typography variant="body1">
                    {billingPreview.userCount} × ${billingPreview.userRate}/month
                    {billingPreview.discount > 0 && ` (${Math.round(billingPreview.discount * 100)}% off)`}
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(billingPreview.userCost)}/month
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            
            <Paper sx={{ p: 2, mb: 3, bgcolor: billingPreview.monthlySavings > 0 ? '#e8f5e9' : '#fff' }}>
              <Grid container alignItems="center">
                <Grid item xs={6}>
                  <Typography variant="subtitle1">
                    New Total:
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {formatCurrency(billingPreview.monthlyTotal)}/month
                  </Typography>
                  {billingPreview.isAnnual && (
                    <Typography variant="body2" color="text.secondary">
                      Billed annually: {formatCurrency(billingPreview.annualTotal)}/year
                    </Typography>
                  )}
                </Grid>
                
                <Grid item xs={6}>
                  {billingPreview.monthlySavings !== 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {billingPreview.monthlySavings > 0 ? (
                        <>
                          <ArrowDown color="#4caf50" size={24} />
                          <Box>
                            <Typography variant="body2" color="success.main">
                              You'll save {formatCurrency(Math.abs(billingPreview.monthlySavings))}/month
                            </Typography>
                            {billingPreview.isAnnual && (
                              <Typography variant="body2" color="success.main" fontWeight="bold">
                                {formatCurrency(Math.abs(billingPreview.annualSavings))}/year in total savings
                              </Typography>
                            )}
                          </Box>
                        </>
                      ) : (
                        <>
                          <ArrowUp color="#f44336" size={24} />
                          <Box>
                            <Typography variant="body2" color="error">
                              Cost will increase by {formatCurrency(Math.abs(billingPreview.monthlySavings))}/month
                            </Typography>
                            {billingPreview.isAnnual && (
                              <Typography variant="body2" color="error">
                                {formatCurrency(Math.abs(billingPreview.annualSavings))}/year total increase
                              </Typography>
                            )}
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Paper>
            
            <Typography variant="subtitle1" gutterBottom>
              Features in {billingPreview.planName} Plan:
            </Typography>
            
            <Grid container spacing={1}>
              {SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan)?.features.map((feature, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle size={16} color="#4CAF50" />
                    <Typography variant="body2">{feature}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseChangePlanDialog}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleConfirmPlanChange}
          disabled={!billingPreview || loading || billingPreview.userShortage > 0}
        >
          Confirm Plan Change
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* Add Payment Method Dialog */}
    <Dialog
      open={addPaymentMethodOpen}
      onClose={handleCloseAddPaymentMethod}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Add Payment Method
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              name="cardNumber"
              label="Card Number"
              value={paymentMethodForm.cardNumber}
              onChange={handlePaymentMethodFormChange}
              fullWidth
              placeholder="1234 5678 9012 3456"
              InputProps={{
                startAdornment: <CreditCard size={18} style={{ marginRight: 8, opacity: 0.7 }} />
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="cardholderName"
              label="Cardholder Name"
              value={paymentMethodForm.cardholderName}
              onChange={handlePaymentMethodFormChange}
              fullWidth
              placeholder="John Doe"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              name="expiryDate"
              label="Expiry Date (MM/YY)"
              value={paymentMethodForm.expiryDate}
              onChange={handlePaymentMethodFormChange}
              fullWidth
              placeholder="MM/YY"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              name="cvv"
              label="CVV"
              value={paymentMethodForm.cvv}
              onChange={handlePaymentMethodFormChange}
              fullWidth
              placeholder="123"
              type="password"
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
          <Shield size={16} />
          <Typography variant="caption" sx={{ ml: 1 }}>
            Your payment information is encrypted and secure.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseAddPaymentMethod}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleAddPaymentMethod}
          disabled={!paymentMethodForm.cardNumber || !paymentMethodForm.cardholderName || !paymentMethodForm.expiryDate || !paymentMethodForm.cvv || loading}
        >
          Add Payment Method
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* User Dialog */}
    <Dialog
      open={userDialogOpen}
      onClose={handleCloseUserDialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {selectedUser ? 'Edit User' : 'Add User'}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              name="name"
              label="Full Name"
              value={userFormData.name}
              onChange={handleUserFormChange}
              fullWidth
              required
              InputProps={{
                startAdornment: <User size={18} style={{ marginRight: 8, opacity: 0.7 }} />
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="email"
              label="Email"
              type="email"
              value={userFormData.email}
              onChange={handleUserFormChange}
              fullWidth
              required
              InputProps={{
                startAdornment: <Mail size={18} style={{ marginRight: 8, opacity: 0.7 }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                name="role"
                value={userFormData.role}
                onChange={handleUserFormChange}
                label="Role"
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="user">Regular User</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={userFormData.status}
                onChange={handleUserFormChange}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {!selectedUser && (
            <Grid item xs={12}>
              <Alert severity="info" icon={<Info size={18} />}>
                An invitation email will be sent to this user with instructions to set up their account.
              </Alert>
            </Grid>
          )}
          
          {userFormData.role === 'admin' && (
            <Grid item xs={12}>
              <Alert severity="info" icon={<Info size={18} />}>
                Admin licenses are billed at ${getCurrentPlan().adminRate}/month per user
                (${userFormData.role === 'admin' && userFormData.status === 'active' && !selectedUser ? `+${formatCurrency(getCurrentPlan().adminRate)}/month` : ''})
              </Alert>
            </Grid>
          )}
          
          {userFormData.role === 'user' && (
            <Grid item xs={12}>
              <Alert severity="info" icon={<Info size={18} />}>
                Regular user licenses are billed at ${getCurrentPlan().userRate}/month per user
                (${userFormData.role === 'user' && userFormData.status === 'active' && !selectedUser ? `+${formatCurrency(getCurrentPlan().userRate)}/month` : ''})
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseUserDialog}>
          Cancel
        </Button>
        <Button
          onClick={handleUserFormSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !userFormData.name || !userFormData.email}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {selectedUser ? 'Update User' : 'Invite User'}
        </Button>
      </DialogActions>
    </Dialog>
  </Box>
);
};

export default CompanyBillingManager;